import React, {ChangeEvent, useCallback, useLayoutEffect, useRef} from 'react';
import {
  AutocompleteChangeDetails,
  FilterOptionsState,
} from '@mui/base/useAutocomplete/useAutocomplete';
import {
  Autocomplete as AutocompleteMui,
  AutocompleteRenderOptionState,
  Box,
  styled,
  SxProps,
  TextField,
  Theme,
} from '@mui/material';
import {
  AutocompleteRenderGetTagProps,
  AutocompleteRenderGroupParams,
} from '@mui/material/Autocomplete/Autocomplete';
import {
  Body,
  Checkbox,
  designSystemToken,
  Icon,
} from '@lightricks/react-design-system';
import Chip from '@/components/chip';
import styles from './AutocompleteInput.module.scss';

const checkboxCheckedSrc = '/assets/svg/checkbox-checked.svg';
const checkboxUncheckedSrc = '/assets/svg/checkbox-unchecked.svg';

export type Option = {
  id: string;
  label: string;
  groupTitle?: string;
  value?: any;
  disabled?: boolean;
};

export type AutocompleteInputProps = {
  testID?: string;
  multiple?: boolean;
  loading?: boolean;
  multipleTextMode?: 'label' | 'chips';
  clearIcon?: React.ReactNode | boolean;
  options: Option[];
  selectedOptions: Option[] | Option | null;
  disableCloseOnSelect?: boolean;
  onChange: (
    event: React.SyntheticEvent,
    value: any,
    reason: string,
    details?: AutocompleteChangeDetails<any>
  ) => void;
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  groupBy?: (option: Option) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  sx?: SxProps<Theme>;
  renderOption?: (
    optionProps: React.HTMLAttributes<HTMLLIElement>,
    option: Option,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  renderOptionLabel?: (option: Option) => React.ReactNode;
  inputValue?: string;
  disabled?: boolean;
  minWidth?: number;
  minHeight?: number;
  filterOptions?: (
    options: Option[],
    state: FilterOptionsState<Option>
  ) => Option[];
  hideInputCursor?: boolean;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onBlur?: (
    event: React.FocusEvent,
    inputValue: string | number | readonly string[] | undefined
  ) => void;
  isOptionEqualToValue?: (option: Option, value: Option) => boolean;
  forcePopupIcon?: boolean;
  selectedOptionVariant?: 'outlined' | 'filled';
  borderWidth?: number;
  chipSize?: 'small' | 'medium';
  openOnFocus?: boolean;
  autoFocus?: boolean;
  selectTextOnFocus?: boolean;
};

const shouldNotForwardProps = ['borderWidth'];

/**
 * Highlights the option label MUI pre-fills into the input, so that typing
 * replaces it instead of appending to it.
 *
 * MUI's own `selectOnFocus` only fires from the Autocomplete root's `onClick`,
 * so it never runs for an input focused programmatically via `autoFocus`.
 * Selecting on focus doesn't work either: MUI writes the label into the input
 * from an effect inside `<Autocomplete>` — a commit the wrapper doesn't
 * re-render in — and writing a value collapses the caret to the end.
 *
 * Rendering this from `renderInput` puts it in that same commit, so its layout
 * effect runs once the label is already in the DOM and the highlight sticks.
 * A plain layout effect in `AutocompleteInput` cannot do the job: the label
 * write is state internal to `<Autocomplete>`, so only the child subtree
 * re-renders and the wrapper's effect only ever sees an empty input.
 */
function InputTextHighlighter({
  inputRef,
  labelToHighlight,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
  labelToHighlight: string;
}) {
  const hasHighlightedRef = useRef(false);

  useLayoutEffect(() => {
    if (
      hasHighlightedRef.current ||
      inputRef.current?.value !== labelToHighlight
    ) {
      return;
    }

    inputRef.current.select();
    hasHighlightedRef.current = true;
  });

  return null;
}

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{borderWidth: number}>`
  .MuiInputBase-root {
    padding-right: ${({className}) =>
      className === 'smallPadding' ? `35px` : 'auto'} !important;

    .MuiOutlinedInput-notchedOutline {
      border-radius: 8px;
      border: ${({borderWidth}) => borderWidth}px solid
        ${({disabled}) =>
          `${designSystemToken('semantic.bg.neutral-secondary')}${
            disabled ? ' !important' : ''
          }`};
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border: ${({borderWidth}) => borderWidth}px solid
        ${({disabled}) =>
          disabled
            ? designSystemToken('semantic.bg.neutral-secondary')
            : 'inherit'} !important;
    }

    .MuiInputBase-input.MuiOutlinedInput-input {
      padding: 0 4px 0 5px;
    }

    &.Mui-focused,
    :hover {
      .MuiOutlinedInput-notchedOutline {
        border: ${({borderWidth}) => borderWidth}px solid
          ${() => designSystemToken('semantic.fg.neutral')};
      }
    }
  }
`;

function AutocompleteInput(props: AutocompleteInputProps) {
  const {
    testID = 'autocomplete-input',
    multiple = false,
    loading = false,
    multipleTextMode = 'chips',
    clearIcon = (
      <Icon
        size="medium"
        appearance="neutral"
        name="Actions-Close-Small"
        color={designSystemToken('semantic.fg.neutral')}
      />
    ),
    options,
    selectedOptions,
    disableCloseOnSelect = true,
    onChange,
    onInputChange,
    placeholder,
    groupBy,
    renderGroup,
    sx,
    renderOption,
    renderOptionLabel,
    inputValue,
    disabled,
    minWidth,
    minHeight = 56,
    filterOptions,
    hideInputCursor,
    onKeyDown,
    onBlur,
    isOptionEqualToValue = (option, _value) => option.id === _value?.id,
    forcePopupIcon = !!options.length,
    selectedOptionVariant = 'filled',
    borderWidth = 1,
    chipSize = 'medium',
    openOnFocus = false,
    autoFocus = false,
    selectTextOnFocus = false,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  // The label MUI pre-fills for the current selection — the text to highlight.
  const preFilledLabel =
    !multiple && selectedOptions ? (selectedOptions as Option).label : '';
  const shouldHighlightPreFilledLabel =
    selectTextOnFocus && autoFocus && !!preFilledLabel;

  const renderPlaceholder = useCallback(() => {
    if (multiple && (selectedOptions as Option[]).length > 0) {
      return undefined;
    }

    return placeholder;
  }, [multiple, placeholder, selectedOptions]);

  const handleRenderTags = (
    value: Option[],
    getTagProps: AutocompleteRenderGetTagProps
  ) => {
    if (multipleTextMode === 'label') {
      return value.map((option: {id: string; label: string}, index: number) => {
        const isLast = index === value.length - 1;
        return (
          <Body key={option.id} size="lg" sx={{whiteSpace: 'pre'}}>
            {option.label}
            {isLast ? null : ', '}
          </Body>
        );
      });
    }

    if (multipleTextMode === 'chips') {
      return value.map(
        (
          option: {id: string; label: string; disabled?: boolean},
          index: number
        ) => (
          <Chip
            label={option.label}
            size={chipSize}
            deleteIcon={
              <Box sx={{display: 'flex'}}>
                <Icon
                  size="medium"
                  appearance="neutral"
                  name="Actions-Close-Small"
                />
              </Box>
            }
            variant={selectedOptionVariant}
            sx={{
              '& .MuiChip-label': {
                fontSize: '14px',
                lineHeight: '18px',
              },
            }}
            {...getTagProps({index})}
            disabled={!!option.disabled || disabled}
            key={option.id}
          />
        )
      );
    }
    return null;
  };

  const handleRenderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement>,
    option: Option,
    state: AutocompleteRenderOptionState
  ) => {
    if (renderOption) {
      return renderOption(optionProps, option, state);
    }

    const optionLabel = renderOptionLabel ? (
      renderOptionLabel(option)
    ) : (
      <span>{option.label}</span>
    );
    const {selected} = state;
    const classNames = [optionProps.className, styles.option];
    if (selected) {
      classNames.push(styles.selected);
    }

    return (
      <li {...optionProps} className={classNames.join(' ')} key={option.id}>
        <div className={styles.content}>
          {multiple ? (
            <div className={styles.checkboxContainer}>
              <Checkbox
                checked={selected}
                onChange={() => {}}
                customCheckedIcon={<img src={checkboxCheckedSrc} alt="" />}
                customUncheckedIcon={<img src={checkboxUncheckedSrc} alt="" />}
              />
            </div>
          ) : null}
          {optionLabel}
        </div>
      </li>
    );
  };

  const getAutocompleteSx = () => {
    return {
      '.MuiInputBase-root': {minWidth, minHeight},
      ...sx,
    };
  };

  return (
    <div data-testid={testID}>
      <AutocompleteMui
        clearIcon={clearIcon}
        options={options}
        freeSolo
        multiple={multiple}
        loading={loading}
        sx={getAutocompleteSx()}
        classes={{popper: styles.popper}}
        forcePopupIcon={forcePopupIcon}
        componentsProps={{
          clearIndicator: {disableRipple: true},
          popupIndicator: {disableRipple: true},
        }}
        popupIcon={
          options.length ? (
            <Icon
              size="medium"
              appearance="neutral"
              name="Actions-Arrow-Down-Small"
              color={
                disabled
                  ? designSystemToken('semantic.fg.inverse-secondary')
                  : designSystemToken('semantic.fg.neutral')
              }
            />
          ) : undefined
        }
        openOnFocus={openOnFocus}
        selectOnFocus={selectTextOnFocus}
        disableCloseOnSelect={disableCloseOnSelect}
        value={selectedOptions}
        inputValue={inputValue}
        onChange={onChange}
        renderTags={handleRenderTags}
        renderInput={(params) => (
          <>
            {shouldHighlightPreFilledLabel ? (
              <InputTextHighlighter
                inputRef={inputRef}
                labelToHighlight={preFilledLabel}
              />
            ) : null}
            <StyledTextField
              {...params}
              disabled={disabled}
              className={!clearIcon ? 'smallPadding' : ''}
              autoFocus={autoFocus}
              inputRef={inputRef}
              placeholder={renderPlaceholder()}
              borderWidth={borderWidth}
              sx={{
                caretColor: hideInputCursor ? 'transparent' : 'inherit',
                cursor: hideInputCursor ? 'default' : 'inherit',
              }}
              inputProps={{
                ...params.inputProps,
                onBlur: (event) => {
                  onBlur?.(event, params.inputProps.value);
                  params.inputProps.onBlur?.(
                    event as React.FocusEvent<HTMLInputElement>
                  );
                },
              }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                onInputChange?.(event);
                params?.inputProps?.onChange?.(event);
              }}
            />
          </>
        )}
        renderOption={handleRenderOption}
        isOptionEqualToValue={isOptionEqualToValue}
        groupBy={groupBy}
        renderGroup={renderGroup}
        disabled={disabled}
        filterOptions={filterOptions}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}

export default AutocompleteInput;
